/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { useTranslate } from "@adapptio/lib-components";
import { IInspectorControlEnumProps, schemaName } from "./InspectorControlEnum.common";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { TFieldDropdownOption } from "../../../ui/components/Fields/FieldDropdown";
import { InvalidModelNode } from "../../../ui/components/Inspector/InvalidModelNode";
import { FieldGroup, FieldSelect } from "@adapptio/lib-editor-ui";
import { isModelValueRequired } from "@adapptio/lib-blueprint";
import { isNil } from "@adapptio/lib-shared";

export const InspectorControlEnum_Select: React.FC<IInspectorControlEnumProps> = ({ model: _model }) => {
	const { model, validationErrors } = useBlueprintModel(_model, schemaName);

	const t = useTranslate();
	const editorInspectorCtx = useEditorInspectorContext();

	const required = isModelValueRequired(model);

	const options = useMemo(() => {
		if (!model) {
			return [];
		}

		return model.schema.opts.options.map((item) => {
			if (item.separator) {
				return {
					isSeparator: true
				} as TFieldDropdownOption;
			} else {
				return {
					label: t("editor", item.label),
					value: String(item.value),
					description: item.description,
					icon: item.icon
				} as TFieldDropdownOption;
			}
		});
	}, [model.schema]);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			let _value;
			if (model?.schema?.opts?.value?.name === "constInteger") {
				_value = parseInt(value);
			} else if (model?.schema?.opts?.value?.name === "constFloat") {
				_value = parseFloat(value);
			} else {
				_value = value;
			}

			model.schema.setValue(model, _value, true);
		},
		[model]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldGroup grow>
			<FieldSelect
				value={!isNil(model.value) ? String(model.value) : model.value}
				options={options}
				onChange={handleChange}
				validationErrors={validationErrors}
				readOnly={editorInspectorCtx?.readOnly}
				clearable={!required}
				placeholder={model.schema.opts.placeholder}
			/>
		</FieldGroup>
	);
};
